
import { useState, FormEvent } from 'react';

const neighborhoods = [
  'West Roseville',
  'Old Town Roseville',
  'Sierra Vista',
  'Woodcreek Oaks',
  'Granite Bay',
  'Sun City',
  'Fiddyment Farm',
  'Blue Oaks',
  'Highland Reserve',
  'Cirby Hills',
  'Maidu',
  'Other',
];

const highlights = [
  'Family-Friendly',
  'Culture & Dining',
  'Remote Work Life',
  'Community Spirit',
  'Active Retirement',
  'Diversity & Jobs',
  'Great Schools',
  'Outdoor Living',
  'Safety & Peace',
  'Affordable Living',
];

export default function SubmitTestimonial() {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [storyLength, setStoryLength] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const firstName = (formData.get('first_name') as string || '').trim();
    const lastInitial = (formData.get('last_initial') as string || '').trim();
    const neighborhood = formData.get('neighborhood') as string;
    const years = (formData.get('years_in_roseville') as string || '').trim();
    const story = (formData.get('story') as string || '').trim();
    const highlight = formData.get('highlight') as string;

    if (!firstName || !lastInitial || !neighborhood || !years || !story || !highlight || rating === 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      const body = new URLSearchParams();
      body.append('first_name', firstName);
      body.append('last_initial', lastInitial);
      body.append('neighborhood', neighborhood);
      body.append('years_in_roseville', years);
      body.append('rating', String(rating));
      body.append('highlight', highlight);
      body.append('story', story);

      await fetch('https://readdy.ai/api/form/d6bluv3v53dtlm7j7og0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      setSubmitted(true);
    } catch {
      // Even on failure we inform the user; you could enhance this with error UI.
      setSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return (
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 py-14">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="flex flex-col items-center gap-5">
            <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-2xl">
              <i className="ri-quill-pen-line text-3xl text-white"></i>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">Share Your Roseville Story</h3>
              <p className="text-teal-100 text-base max-w-xl mx-auto">
                Love living in Roseville? We&rsquo;d love to hear from you! Submit your testimonial and inspire others to discover our amazing community.
              </p>
            </div>
            <button
              onClick={() => setIsOpen(true)}
              className="mt-2 inline-flex items-center gap-2 px-8 py-3.5 bg-white text-teal-700 font-semibold rounded-lg hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap shadow-lg"
            >
              <i className="ri-edit-line text-lg"></i>
              Submit Your Testimonial
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-stone-50 to-white py-20">
      <div className="max-w-2xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 rounded-full mb-5">
            <i className="ri-quill-pen-line text-teal-600"></i>
            <span className="text-teal-700 text-sm font-semibold tracking-wide uppercase">Your Voice Matters</span>
          </div>
          <h3 className="text-3xl font-bold text-gray-900 mb-3">Submit Your Testimonial</h3>
          <p className="text-gray-600 max-w-lg mx-auto">
            Tell us what makes Roseville special to you. Your story could help someone find their new home.
          </p>
        </div>

        {submitted ? (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-teal-50 rounded-full mx-auto mb-6">
              <i className="ri-check-line text-4xl text-teal-600"></i>
            </div>
            <h4 className="text-2xl font-bold text-gray-900 mb-3">Thank You!</h4>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Your testimonial has been submitted successfully. We appreciate you sharing your Roseville story with the community!
            </p>
            <button
              onClick={() => {
                setIsOpen(false);
                setSubmitted(false);
                setRating(0);
                setStoryLength(0);
              }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Testimonials
            </button>
          </div>
        ) : (
          <form
            id="submit-testimonial-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 md:p-10 space-y-6"
          >
            {/* Name Row */}
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="first_name"
                  required
                  placeholder="e.g. Sarah"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Last Initial <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="last_initial"
                  required
                  maxLength={1}
                  placeholder="e.g. C"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            {/* Neighborhood & Years */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Neighborhood <span className="text-red-500">*</span>
                </label>
                <select
                  name="neighborhood"
                  required
                  defaultValue=""
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all appearance-none bg-white cursor-pointer"
                >
                  <option value="" disabled>Select neighborhood</option>
                  {neighborhoods.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Years in Roseville <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="years_in_roseville"
                  required
                  min={0}
                  max={80}
                  placeholder="e.g. 5"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            {/* Rating */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Your Rating <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="w-10 h-10 flex items-center justify-center cursor-pointer transition-transform hover:scale-110"
                  >
                    <i
                      className={`text-2xl ${
                        star <= (hoverRating || rating)
                          ? 'ri-star-fill text-amber-400'
                          : 'ri-star-line text-gray-300'
                      }`}
                    ></i>
                  </button>
                ))}
                {rating > 0 && (
                  <span className="ml-2 text-sm text-gray-500">
                    {rating === 5 ? 'Excellent!' : rating === 4 ? 'Great!' : rating === 3 ? 'Good' : rating === 2 ? 'Fair' : 'Poor'}
                  </span>
                )}
              </div>
            </div>

            {/* Highlight */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                What do you love most? <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {highlights.map((h) => (
                  <label key={h} className="cursor-pointer">
                    <input type="radio" name="highlight" value={h} required className="sr-only peer" />
                    <span className="inline-block px-4 py-2 text-sm border border-gray-200 rounded-full peer-checked:bg-teal-600 peer-checked:text-white peer-checked:border-teal-600 text-gray-600 hover:border-teal-300 transition-all whitespace-nowrap">
                      {h}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Story */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Your Story <span className="text-red-500">*</span>
              </label>
              <textarea
                name="story"
                required
                rows={5}
                maxLength={500}
                placeholder="Share what makes Roseville special to you — your favorite spots, community experiences, why you chose to live here..."
                onChange={(e) => setStoryLength(e.target.value.length)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none"
              ></textarea>
              <div className="flex justify-end mt-1">
                <span className={`text-xs ${storyLength > 450 ? 'text-amber-500' : 'text-gray-400'}`}>
                  {storyLength}/500
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  setRating(0);
                  setStoryLength(0);
                }}
                className="text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center gap-2 px-8 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    Submit Testimonial
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
